import { http } from "../../utils/config";


const getStatusText = (status) => {
  const map = {
    PLANNED: "Đã lên kế hoạch",
    ONGOING: "Đang diễn ra",
    CLOSED: "Đã kết thúc",
  };
  return map[status] || status;
};

const campainAPI = {
  // Get all campains with pagination and search
  getCampains: (params = {}) => {
    const {
      page = 1,
      per_page = 15,
      search = '',
      status = '',
      sort_by = 'created_at',
      sort_order = 'desc',
      start_date = '',
      end_date = ''
    } = params;

    const queryParams = new URLSearchParams({
      page: page.toString(),
      per_page: per_page.toString(),
      sort_by,
      sort_order,
      ...(search && { search }),
      ...(status !== '' && { status }),
      ...(start_date && { start_date }),
      ...(end_date && { end_date })
    });

    return http.get(`/api/v1/campains?${queryParams}`);
  },

  // Get campain by ID
  getCampainById: (id) => {
    return http.get(`/api/v1/campains/${id}`);
  },

  // Create new campain
  createCampain: (campainData) => {
    return http.post('/api/v1/campains', campainData);
  },

  // Update campain
  updateCampain: (id, campainData) => {
    return http.put(`/api/v1/campains/${id}`, campainData);
  },

  // Delete campain
  deleteCampain: (id) => {
    return http.delete(`/api/v1/campains/${id}`);
  },

  // Toggle campain status
  toggleCampainStatus: (id, status) => {
    return http.post(`/api/v1/campains/${id}/status`, {
      status: status
    });
  },

  // Duplicate campain
  duplicateCampain: (id, options = {}) => {
    return http.post(`/api/v1/campains/${id}/duplicate`, options);
  },

  // Get statistics
  getCampainStats: (id) => {
    return http.get(`/api/v1/campains/${id}/stats`);
  },

};

// Helper functions for handling API responses
export const campainHelpers = {
  // Extract campain data from API response
  extractCampainData: (response) => {
    if (response.data && response.data.success) {
      return response.data.data;
    }
    throw new Error(response.data?.message || 'Unknown error occurred');
  },

  // Extract paginated campain list from API response
  extractCampainList: (response) => {
    if (response.data && response.data.success) {
      const data = response.data.data;
      return {
        campains: data.data || [],
        pagination: {
          current: data.current_page,
          pageSize: data.per_page,
          total: data.total,
          from: data.from,
          to: data.to,
          lastPage: data.last_page,
          nextPageUrl: data.next_page_url,
          prevPageUrl: data.prev_page_url
        }
      };
    }
    throw new Error(response.data?.message || 'Unknown error occurred');
  },

  extractCusFeeList: (response) => {
    if (response.data && response.data.success) {
      const data = response.data.data;
      return {
        fees: data.data || [],
        pagination: {
          current: data.current_page,
          pageSize: data.per_page,
          total: data.total,
          from: data.from,
          to: data.to,
          lastPage: data.last_page,
          nextPageUrl: data.next_page_url,
          prevPageUrl: data.prev_page_url
        }
      };
    }
    throw new Error(response.data?.message || 'Unknown error occurred');
  },

  // Format campain data for form submission
  formatCampainData: (formData) => {
    return {
      name: formData.name?.trim(),
      description: formData.description?.trim(),
      start_date: formData.start_date,
      end_date: formData.end_date,
      status: formData.status ?? "PLANNED",
      customer_fees: formData.customer_fees || [],
      fee_definitions: formData.fee_definitions || [],
      is_send_zns: formData.is_send_zns,
      send_schedule_at:formData.send_schedule_at,
      before_date: formData.before_date,
      after_date: formData.after_date,
      content: formData.content?.trim(),
      zns_oa_name: formData.zns_oa_name,
      zns_template_id: formData.zns_template_id,
      invoice_cnf_id: formData.invoice_cnf_id,
    };
  },

  // Validate campain data before submission
  validateCampainData: (campainData) => {
    const errors = {};

    if (!campainData.name?.trim()) {
      errors.name = 'Tên đợt thu không được để trống';
    }

    if (!campainData.start_date) {
      errors.start_date = 'Ngày bắt đầu không được để trống';
    }

    if (!campainData.end_date) {
      errors.end_date = 'Ngày kết thúc không được để trống';
    }

    if (campainData.start_date && campainData.end_date) {
      const startDate = new Date(campainData.start_date);
      const endDate = new Date(campainData.end_date);

      if (endDate <= startDate) {
        errors.end_date = 'Ngày kết thúc phải sau ngày bắt đầu';
      }
    }

    if (campainData.description && campainData.description.length > 1000) {
      errors.description = 'Mô tả không được vượt quá 1000 ký tự';
    }

    // Validate customer_fees
    if (!campainData.customer_fees || campainData.customer_fees.length === 0) {
      errors.customer_fees = 'Phải có ít nhất một khách hàng trong đợt thu';
    } else {
      campainData.customer_fees.forEach((customerFee, index) => {
        if (!customerFee.customer_id) {
          errors[`customer_fees.${index}.customer_id`] = 'ID khách hàng không được để trống';
        }

      });
    }

    // if (!campainData.invoice_cnf_id) {
    //   errors.invoice_cnf_id = 'Vui lòng chọn tài khoản xuất hóa đơn!';
    // }

    return {
      isValid: Object.keys(errors).length === 0,
      errors
    };
  },


  // Format campain for display
  formatCampainForDisplay: (campain) => {
    return {
      ...campain,
      statusText: getStatusText(campain.status),
      startDate: new Date(campain.start_date).toLocaleDateString('vi-VN'),
      endDate: new Date(campain.end_date).toLocaleDateString('vi-VN'),
      createdAt: new Date(campain.created_at).toLocaleDateString('vi-VN'),
      updatedAt: new Date(campain.updated_at).toLocaleDateString('vi-VN'),
    };
  },

  // Validate fee structure
  validateFeeStructure: (fees) => {
    const errors = {};
    const allowedFeeTypes = ['K1', 'K2', 'K3']; // Có thể customize theo yêu cầu

    Object.entries(fees).forEach(([feeType, amount]) => {
      if (!allowedFeeTypes.includes(feeType)) {
        errors[feeType] = `Loại phí ${feeType} không được phép`;
      }

    });

    return {
      isValid: Object.keys(errors).length === 0,
      errors
    };
  },

  // Generate campain name suggestion
  generateCampainName: (month, year) => {
    return `Đợt thu tháng ${month}/${year}`;
  },

  // Check if campain is active
  isCampainActive: (campain) => {
    if (!campain.status) return false;

    const now = new Date();
    const startDate = new Date(campain.start_date);
    const endDate = new Date(campain.end_date);

    return now >= startDate && now <= endDate;
  },

  
  // Validate duplicate options
  validateDuplicateOptions: (options) => {
    const errors = {};

    if (options.name && options.name.trim().length === 0) {
      errors.name = 'Tên đợt thu không được để trống';
    }

    if (options.name && options.name.length > 255) {
      errors.name = 'Tên đợt thu không được vượt quá 255 ký tự';
    }

    if (options.start_date && options.end_date) {
      const startDate = new Date(options.start_date);
      const endDate = new Date(options.end_date);

      if (endDate <= startDate) {
        errors.end_date = 'Ngày kết thúc phải sau ngày bắt đầu';
      }
    }

    return {
      isValid: Object.keys(errors).length === 0,
      errors
    };
  },

  // Generate duplicate name
  generateDuplicateName: (originalName) => {
    const timestamp = new Date().toLocaleDateString('vi-VN');
    return `${originalName} (Sao chép ${timestamp})`;
  },
};

// Error handling wrapper
export const withErrorHandling = (apiFunction) => {
  return async (...args) => {
    try {
      const response = await apiFunction(...args);
      return response;
    } catch (error) {
      console.error('API Error:', error);

      // Handle different types of errors
      if (error.response) {
        // Server responded with error status
        const { status, data } = error.response;
        switch (status) {
          case 400:
            throw new Error(data.message || 'Dữ liệu không hợp lệ');
          case 401:
            throw new Error('Bạn cần đăng nhập để thực hiện thao tác này');
          case 403:
            throw new Error('Bạn không có quyền thực hiện thao tác này');
          case 404:
            throw new Error('Không tìm thấy đợt thu');
          case 409:
            throw new Error('Đợt thu trong khoảng thời gian này đã tồn tại');
          case 422:
            throw new Error(data.message || 'Dữ liệu không hợp lệ');
          case 500:
            throw new Error(data.message || "Lỗi server, vui lòng thử lại sau");
          default:
            throw new Error(data.message || 'Đã có lỗi xảy ra');
        }
      } else if (error.request) {
        // Network error
        throw new Error('Không thể kết nối đến server');
      } else {
        // Other error
        throw new Error(error.message || 'Đã có lỗi xảy ra');
      }
    }
  };
};

// Wrapped API functions with error handling
export const campainAPIWithErrorHandling = {
  getCampains: withErrorHandling(campainAPI.getCampains),
  getCampainById: withErrorHandling(campainAPI.getCampainById),
  createCampain: withErrorHandling(campainAPI.createCampain),
  updateCampain: withErrorHandling(campainAPI.updateCampain),
  deleteCampain: withErrorHandling(campainAPI.deleteCampain),
  toggleCampainStatus: withErrorHandling(campainAPI.toggleCampainStatus),
  duplicateCampain: withErrorHandling(campainAPI.duplicateCampain),
  getCampainStats: withErrorHandling(campainAPI.getCampainStats),
};

export default campainAPI;